import { CapacitorHttp } from '@capacitor/core';

import { importExam } from '../persistence/importExam';
import type { Store } from '../persistence/port';
import type { ServerClient } from './client';
import {
  RelayPruefungUnlesbar,
  zuBundle,
  type RelayDokument,
  type RelayKopf,
  type RelayPersonen,
} from './dokument';
import { holeMedien, type MediumZumHolen } from './medienAblage';

/**
 * Eine Prüfung von Relay holen und ablegen — Etappe 2 der Phase 4.
 *
 * ⚠️ **Das ging bis zum 2026-09-16 nicht.** `docs/spec-phase4-server.md` §3 hielt fest, Relay
 * liefere unter `/exam/get` nur die `Exam`-Zeile und für den Inhalt gebe es keinen Endpunkt.
 * Seit `38c474b` gibt es ihn: `/exam/document` trägt den ganzen Baum. Damit fährt auch das
 * zweite Gleis (s. `memory/zweigleisig.md`).
 *
 * **Zwei Abrufe, in dieser Reihenfolge:**
 *
 * | Abruf | Wofür |
 * | --- | --- |
 * | `/exam/available` | Kopfdaten: Art, Datum, Zeiten, Änderungsstand |
 * | `/exam/document` | der Inhaltsbaum samt Prüflingen, Medien, Prüfern und Schauspielern |
 *
 * ⛔ **`/exam/assignments`, `/examiner/list` und `/actor/list` ruft das Gerät nicht mehr.**
 * Sie weisen ein Gerätetoken mit `403 NOT_AN_OPERATOR` ab und bleiben der Verwaltung
 * vorbehalten. Seit Relay-Commit `a8e198e` (2026-10-02, Entscheidung des Nutzers bei Relay)
 * stehen die Personen dieser einen Prüfung stattdessen im Dokument (`examiners`, `actors`).
 * Bis dahin kam jede Relay-Prüfung ohne Prüfer an, und keine Station liess sich starten.
 *
 * ⚠️ **Kopfdaten aus `/exam/available`, nicht aus `/exam/get`.** Letzteres gibt die ganze
 * `Exam`-Zeile heraus, einschliesslich `examPin` und `authentificationSwitchPin`. Auf ein
 * Prüfergerät gehört beides nicht, und `available` trägt jedes Feld, das hier gebraucht wird.
 */

export const PFAD = {
  available: '/rest/v1/exam/available',
  document: '/rest/v1/exam/document',
  datei: '/rest/v1/media/datei',
} as const;

interface AvailableAntwort {
  exams?: RelayKopf[];
}

export interface RelayDownloadErgebnis {
  examId: string;
  medien: MediumZumHolen[];
  /** Siehe `dokument.ts::RelayAbbildung` — die Prüfung kam ohne Punktzahlen an. */
  ohnePunkte: boolean;
}

export async function ladeRelayExam(
  client: ServerClient,
  store: Store,
  examId: string,
  /**
   * Wie eine Mediendatei geholt wird — `relayMedienHoler(...)` auf dem Gerät, etwas
   * Gefälschtes im Test. Fehlt es, bleiben die Sätze in `Media` stehen und die Dateien aus.
   */
  hole?: (id: string) => Promise<string>,
  /**
   * Das Prüfungspasswort, falls die Prüfung eins hat (`passwordProtected`). Geht als
   * `X-Exam-Password` mit; fehlt oder stimmt es nicht, antwortet Relay 403 mit
   * `EXAM_PASSWORD_REQUIRED` bzw. `EXAM_PASSWORD_WRONG` (Relay `00426bb`).
   */
  passwort?: string,
): Promise<RelayDownloadErgebnis> {
  const [kopfAntwort, dokument] = await Promise.all([
    client.get<AvailableAntwort>(PFAD.available),
    client.get<RelayDokument>(
      PFAD.document,
      { id: examId },
      passwort ? { 'X-Exam-Password': passwort } : undefined,
    ),
  ]);

  const kopf = (kopfAntwort?.exams ?? []).find((e) => e.id === examId);
  if (!kopf) {
    // ⚠️ Nicht stillschweigend mit leeren Kopfdaten weitermachen: ohne Datum und Art stünde
    // die Prüfung später ohne Zeitfenster da, und niemand sähe, woran es lag.
    throw new RelayPruefungUnlesbar(
      'Die Prüfung steht nicht in der Übersicht des Servers. Vielleicht wurde sie inzwischen entfernt.',
    );
  }

  // Ein älterer Relay führt die beiden Felder nicht: dann kommt die Prüfung ohne Personen.
  const personen: RelayPersonen = {
    examiners: dokument?.examiners ?? [],
    actors: dokument?.actors ?? [],
  };

  const { bundle, medien, ohnePunkte } = zuBundle(dokument ?? {}, kopf, personen);

  // Erst wenn alles beisammen ist, wird geschrieben — `importExam` läuft in einer Transaktion.
  // Bräche die Abbildung mittendrin ab, stünde sonst eine halbe Prüfung in der Ablage.
  await importExam(store, bundle);

  // Erst nach dem Import: die Dateien hängen an Sätzen, die dann schon stehen — und ein
  // Abbruch beim Holen lässt eine vollständige Prüfung zurück, nur ohne Anhänge.
  if (hole && medien.length > 0) {
    await holeMedien(medien, { examId: bundle.exam.id, hole });
  }

  return { examId: bundle.exam.id, medien, ohnePunkte };
}

/**
 * Der Weg hinaus für die Mediendateien.
 *
 * ⚠️ **Nicht über den `ServerClient`**: dessen Transport reicht Text durch, und ein JPEG als
 * Text gelesen ist verloren. `CapacitorHttp` mit `responseType: 'blob'` gibt die Bytes als
 * Base64 zurück — auf dem Gerät wie im Browser.
 *
 * ⚠️ **Das Token kommt vom angemeldeten Client.** Anders als Classic (HTTP Basic) meldet sich
 * Relay mit einem Bearer-Token an; ohne Anmeldung gäbe es hier einen 401 statt eines Bildes.
 */
export function relayMedienHoler(baseUrl: string, token: string) {
  return async (id: string): Promise<string> => {
    const antwort = await CapacitorHttp.request({
      url: baseUrl.replace(/\/+$/, '') + PFAD.datei + '?id=' + encodeURIComponent(id),
      method: 'GET',
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob',
      connectTimeout: 30000,
      readTimeout: 30000,
    });
    if (antwort.status < 200 || antwort.status >= 300) {
      throw new Error('Der Server antwortete ' + antwort.status + '.');
    }
    if (typeof antwort.data !== 'string' || antwort.data === '') {
      throw new Error('Der Server lieferte keine Daten.');
    }
    // Manche Wege legen einen `data:`-Vorspann davor; die Ablage will nur die Bytes.
    const komma = antwort.data.indexOf(',');
    return antwort.data.startsWith('data:') && komma > 0 ? antwort.data.slice(komma + 1) : antwort.data;
  };
}
